import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Pause, Play } from "lucide-react";
import { crossesSection, stepSection } from "../../lib/sectionFlow";
import "./FilmMode.css";

/** Screen-heights per second inside a scene: slow enough to watch every morph. */
const SPEED = 0.17;
/** How long to hold on a finished scene before cutting to the next, in ms. */
const SECTION_HOLD = 2500;
/**
 * The cut: between two scenes nothing morphs — one section just slides off —
 * so instead of crawling through that stretch the film glides straight to the
 * start of the next scene, like a cut between shots.
 */
const CUT_MS = 750;
const easeInOut = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
/** Progress ring geometry. */
const R = 19;
const CIRC = 2 * Math.PI * R;

/**
 * "Play" button: scrolls the page slowly from where the reader is to the end,
 * pausing briefly at the end of each section, so the site plays like a film.
 *
 * It only ever drives the native scroll position, so every scene, reveal and
 * idle loop runs exactly as it does under a hand on the wheel. Any input from
 * the reader — wheel, touch, keys, a click elsewhere — hands control back.
 */
export default function FilmMode() {
    const { pathname } = useLocation();
    const [playing, setPlaying] = useState(false);
    const ringRef = useRef<SVGCircleElement>(null);
    const run = useRef({
        raf: 0,
        y: 0,
        last: 0,
        holdUntil: 0,
        stops: [] as number[],
        scenes: [] as [number, number][],
        cut: null as null | { from: number; to: number; start: number },
        /** A section transition is running (scene mode); wait for it. */
        waiting: false,
    });

    const stop = useCallback(() => setPlaying(false), []);

    // Progress ring follows the scroll position, playing or not.
    useEffect(() => {
        const ring = ringRef.current;
        if (!ring) return;
        const paint = () => {
            const max = document.documentElement.scrollHeight - window.innerHeight;
            const p = max > 0 ? Math.min(1, window.scrollY / max) : 0;
            ring.style.strokeDashoffset = String(CIRC * (1 - p));
        };
        paint();
        window.addEventListener("scroll", paint, { passive: true });
        window.addEventListener("resize", paint);
        return () => {
            window.removeEventListener("scroll", paint);
            window.removeEventListener("resize", paint);
        };
    }, [pathname]);

    useEffect(() => {
        if (!playing) return;
        const html = document.documentElement;
        const r = run.current;
        const maxY = () => html.scrollHeight - window.innerHeight;

        // Starting at (or near) the end replays from the top.
        if (window.scrollY >= maxY() - 2) window.scrollTo(0, 0);

        // Where each scroll scene is pinned and playing, [start, end]. Story
        // mode only; on the plain layout there are none.
        r.scenes = Array.from(document.querySelectorAll<HTMLElement>(".scene-track")).map((el) => {
            const top = el.getBoundingClientRect().top + window.scrollY;
            return [top, top + el.offsetHeight - window.innerHeight] as [number, number];
        });

        // Where to hold: the end of each scene, or — on the plain layout — of
        // each section. Read once; nothing moves while playing.
        const ends = r.scenes.length
            ? r.scenes.map(([, b]) => b)
            : Array.from(document.querySelectorAll<HTMLElement>("main .section")).map(
                  (el) => el.getBoundingClientRect().bottom + window.scrollY - window.innerHeight
              );
        r.stops = ends
            // Every end still ahead, including one the reader is already
            // at: the film always holds there rather than drifting past it.
            .filter((y, i, all) => y >= window.scrollY - 0.5 && all.indexOf(y) === i)
            .sort((a, b) => a - b);

        // Smooth-scroll would turn every frame's small step into its own glide.
        const prevBehavior = html.style.scrollBehavior;
        html.style.scrollBehavior = "auto";
        r.y = window.scrollY;
        r.last = performance.now();
        r.holdUntil = 0;
        r.cut = null;
        r.waiting = false;

        /** The start of the next scene after `y`, if `y` is between scenes. */
        const nextScene = (y: number) => {
            if (!r.scenes.length || r.scenes.some(([a, b]) => y >= a - 1 && y < b)) return null;
            const next = r.scenes.find(([a]) => a > y);
            return next ? next[0] : null;
        };

        const tick = (now: number) => {
            const dt = Math.min(0.05, (now - r.last) / 1000);
            r.last = now;
            if (r.waiting) {
                r.raf = requestAnimationFrame(tick);
                return;
            }
            if (r.cut) {
                // Mid-cut: glide to the next scene, then play on from there.
                const t = Math.min(1, (now - r.cut.start) / CUT_MS);
                r.y = r.cut.from + (r.cut.to - r.cut.from) * easeInOut(t);
                if (t >= 1) r.cut = null;
                window.scrollTo(0, r.y);
            } else if (now >= r.holdUntil) {
                const target = nextScene(r.y);
                if (target !== null && crossesSection(r.y, target)) {
                    // Into another section: the same transition a reader gets.
                    r.waiting = true;
                    stepSection(1).then(() => {
                        r.y = window.scrollY;
                        r.waiting = false;
                    });
                    r.raf = requestAnimationFrame(tick);
                    return;
                }
                if (target !== null) {
                    r.cut = { from: r.y, to: target, start: now };
                    r.raf = requestAnimationFrame(tick);
                    return;
                }
                const next = r.y + SPEED * window.innerHeight * dt;
                const stopAt = r.stops[0];
                if (stopAt !== undefined && next >= stopAt) {
                    r.y = stopAt;
                    r.stops.shift();
                    r.holdUntil = now + SECTION_HOLD;
                } else {
                    r.y = next;
                }
                if (r.y >= maxY()) {
                    window.scrollTo(0, maxY());
                    setPlaying(false);
                    return;
                }
                window.scrollTo(0, r.y);
            }
            r.raf = requestAnimationFrame(tick);
        };
        r.raf = requestAnimationFrame(tick);

        // The reader taking over ends the film.
        const takeOver = () => setPlaying(false);
        const onKey = (e: KeyboardEvent) => {
            if (["Tab", "Shift", "Control", "Alt", "Meta"].indexOf(e.key) < 0) setPlaying(false);
        };
        const onDown = (e: PointerEvent) => {
            if (!(e.target as Element | null)?.closest(".film-btn")) setPlaying(false);
        };
        window.addEventListener("wheel", takeOver, { passive: true });
        window.addEventListener("touchstart", takeOver, { passive: true });
        window.addEventListener("keydown", onKey);
        window.addEventListener("pointerdown", onDown);

        return () => {
            cancelAnimationFrame(r.raf);
            html.style.scrollBehavior = prevBehavior;
            window.removeEventListener("wheel", takeOver);
            window.removeEventListener("touchstart", takeOver);
            window.removeEventListener("keydown", onKey);
            window.removeEventListener("pointerdown", onDown);
        };
    }, [playing]);

    // Leaving the page stops the film.
    useEffect(stop, [pathname, stop]);

    if (pathname !== "/") return null;

    return (
        <button
            type="button"
            className={`film-btn${playing ? " is-playing" : ""}`}
            onClick={() => setPlaying((v) => !v)}
            aria-pressed={playing}
            aria-label={playing ? "Pause the auto-scroll" : "Play the page like a film"}
        >
            <svg className="film-ring" viewBox="0 0 44 44" aria-hidden="true">
                <circle className="film-ring-track" cx="22" cy="22" r={R} />
                <circle
                    ref={ringRef}
                    className="film-ring-fill"
                    cx="22"
                    cy="22"
                    r={R}
                    style={{ strokeDasharray: CIRC, strokeDashoffset: CIRC }}
                />
            </svg>
            <span className="film-icon">
                {playing ? <Pause size={15} strokeWidth={2.2} /> : <Play size={15} strokeWidth={2.2} />}
            </span>
            <span className="film-label mono">{playing ? "Pause" : "Play"}</span>
        </button>
    );
}
